import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Store, ChevronRight, FileText, HelpCircle, Mail, MapPin, X } from "lucide-react";

import { agentApi } from "@/lib/extensions-api";
import { Skeleton } from "@/components/ui/Skeleton";

interface RightSidebarProps {
  open: boolean;
  onClose: () => void;
}

export function RightSidebar({ open, onClose }: RightSidebarProps) {
  // Fetch active vendors via the Public Endpoint
  const { data: vendors, isLoading } = useQuery({
    queryKey: ["public-vendors"],
    queryFn: agentApi.publicVendors, 
  });

  return (
    <>
      {/* 📱 Overlay Backdrop (Visible when drawer is open on ANY screen size) */}
      {open && (
        <div 
          className="fixed inset-0 z-40 bg-surface-900/60 backdrop-blur-sm transition-opacity" 
          onClick={onClose} 
        />
      )}

      {/* 💻 Right Drawer Panel (Floats OVER the page, Full Collapsible) */}
      <aside 
        className={`fixed top-0 right-0 z-50 h-screen w-80 bg-white border-l border-surface-200 shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-5 border-b border-surface-200 shrink-0 bg-surface-50/50">
          <h2 className="font-extrabold text-surface-900 flex items-center gap-2">
            <Store className="w-5 h-5 text-brand-600"/> Official Shops
          </h2>
          <button 
            onClick={onClose} 
            className="p-2 rounded-lg text-surface-500 hover:bg-danger-50 hover:text-danger-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Content Area */}
        <div className="flex-1 overflow-y-auto">
          <ul className="p-4 space-y-3">
            {isLoading ? (
              <>
                <Skeleton className="h-16 w-full rounded-2xl" />
                <Skeleton className="h-16 w-full rounded-2xl" />
                <Skeleton className="h-16 w-full rounded-2xl" />
              </>
            ) : vendors && vendors.length > 0 ? (
              vendors.map((vendor) => {
                // Fallback to user's full name if shop_name is empty
                const displayName = vendor.shop_name || `${vendor.full_name}'s Shop`;
                
                return (
                  <li key={vendor.id}>
                    <Link 
                      to={`/catalog?vendor=${vendor.id}`} 
                      onClick={onClose} 
                      className="group flex items-center justify-between p-3 rounded-2xl bg-white border border-surface-200 hover:border-brand-300 hover:shadow-md transition-all duration-300"
                    >
                      <div className="flex items-center gap-3">
                        {vendor.shop_logo ? (
                          <img src={vendor.shop_logo} alt={displayName} className="w-12 h-12 rounded-xl object-cover bg-surface-50 shadow-sm" />
                        ) : (
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-surface-100 to-surface-200 text-surface-600 flex items-center justify-center font-bold text-lg shadow-sm border border-surface-200/50">
                            {displayName.charAt(0)}
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-extrabold text-surface-900 leading-tight group-hover:text-brand-600 transition-colors line-clamp-1">{displayName}</div>
                          <div className="text-[10px] text-surface-500 flex items-center gap-1 mt-1 font-semibold uppercase tracking-wider">
                            <MapPin className="w-3 h-3 text-brand-500"/> Verified Agent
                          </div>
                        </div>
                      </div>
                      <div className="w-8 h-8 rounded-lg bg-surface-50 flex items-center justify-center group-hover:bg-brand-50 transition-colors shrink-0">
                        <ChevronRight className="w-4 h-4 text-surface-400 group-hover:text-brand-600 transition-colors" />
                      </div>
                    </Link>
                  </li>
                );
              })
            ) : (
              <div className="text-center text-sm text-surface-500 py-10 border-2 border-dashed border-surface-200 rounded-2xl bg-surface-50/50">
                <Store className="w-10 h-10 text-surface-300 mx-auto mb-3" />
                <span className="font-medium">No official shops available right now.</span>
              </div>
            )}
          </ul>
          
          {/* Support Links */}
          <div className="p-5 border-t border-surface-100 mt-2 bg-surface-50/50">
            <h3 className="text-[10px] font-bold uppercase tracking-widest text-surface-400 mb-4 px-1">Support & Info</h3>
            <ul className="space-y-2">
              <li>
                <Link to="#" className="flex items-center gap-3 p-3 rounded-xl bg-white border border-transparent hover:border-surface-200 hover:shadow-sm text-surface-700 hover:text-brand-600 font-semibold text-sm transition-all group">
                  <div className="w-8 h-8 rounded-lg bg-surface-100 flex items-center justify-center group-hover:bg-brand-50 transition-colors"><FileText className="w-4 h-4 text-surface-500 group-hover:text-brand-500"/></div>
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link to="#" className="flex items-center gap-3 p-3 rounded-xl bg-white border border-transparent hover:border-surface-200 hover:shadow-sm text-surface-700 hover:text-brand-600 font-semibold text-sm transition-all group">
                  <div className="w-8 h-8 rounded-lg bg-surface-100 flex items-center justify-center group-hover:bg-brand-50 transition-colors"><HelpCircle className="w-4 h-4 text-surface-500 group-hover:text-brand-500"/></div>
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="#" className="flex items-center gap-3 p-3 rounded-xl bg-white border border-transparent hover:border-surface-200 hover:shadow-sm text-surface-700 hover:text-brand-600 font-semibold text-sm transition-all group">
                  <div className="w-8 h-8 rounded-lg bg-surface-100 flex items-center justify-center group-hover:bg-brand-50 transition-colors"><Mail className="w-4 h-4 text-surface-500 group-hover:text-brand-500"/></div>
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </aside>
    </>
  );
}